import {
  Backdrop,
  Box,
  ClickAwayListener,
  css,
  Stack,
  useMediaQuery
} from '@mui/material'
import { MouseEvent, useContext, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import Magnifier from 'assets/icons/nucleo/18px_magnifier-3.svg'
import { ButtonVariant } from 'components/common/Button'
import { themeUtils } from 'styles/theme'
import { DateTabContent } from './DateTabContent'
import { LocationTabContent } from './LocationTabContent'
import { MobileContent } from './MobileContent'
import {
  MiniButton,
  MiniSearchBar,
  MobileSearchBarHeader,
  SearchBarContainer,
  SearchBarContent,
  SearchElement,
  SearchBarHeader as StyledSearchBarHeader
} from './SearchBar.style'
import { SearchBarContext } from './SearchBarContext'
import { SearchBarHeader, SearchTab } from './SearchBarHeader'
import { SearchBarMiniHeaderContent } from './SearchBarMiniHeaderContent'
import { TypeTabContent } from './TypeTabContent'

interface SearchBarProps {
  forcedExpanded?: boolean
  hasTop?: boolean
}

export const SearchBar = ({ forcedExpanded, hasTop }: SearchBarProps) => {
  const [expanded, setExpanded] = useState(forcedExpanded || false)
  const [open, setOpen] = useState(false)
  const [searchTab, setSearchTab] = useState<SearchTab>(null)

  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const { submitSearch, location } = useContext(SearchBarContext)

  useEffect(() => {
    if (location) {
      isMobile ? setSearchTab('type') : setOpen(false)
    }
  }, [location])

  const handleClickAway = () => {
    if (!forcedExpanded) {
      setExpanded(false)
    }
    setOpen(false)
    setSearchTab(null)
  }

  const handlePrevious = () => {
    if (searchTab === 'type') {
      handleClickAway()
    } else {
      setSearchTab('type')
    }
  }

  const handleExpand = () => {
    setExpanded(true)
    setOpen(true)
    setSearchTab('type')
  }

  const handleOpen = (tabType: SearchTab) => {
    setOpen(true)
    setSearchTab(tabType)
  }

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    submitSearch()
  }

  return (
    <>
      {!isMobile && expanded && !forcedExpanded && (
        <Backdrop
          open
          sx={{ zIndex: 100 }}
          onClick={(e) => e.preventDefault()}
        />
      )}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack
          alignItems="center"
          css={css`
            ${!forcedExpanded &&
            !open &&
            css`
              width: 100%;
            `}
            ${themeUtils.maxWidth!.md} {
              width: 100%;
            }
          `}
        >
          {expanded ? (
            <SearchBarContainer
              $forcedExpanded={forcedExpanded}
              $hasTop={hasTop}
              alignItems="center"
            >
              <SearchBarHeader
                forcedExpanded={forcedExpanded}
                expanded={expanded}
                handleOpen={handleOpen}
                close={handleClickAway}
                open={open}
                searchTab={searchTab}
              />
              {!isMobile &&
                open &&
                match(searchTab)
                  .with('type', () => (
                    <SearchBarContent
                      $forcedExpanded={forcedExpanded}
                      width="100%"
                    >
                      <TypeTabContent
                        close={handleClickAway}
                        setSearchTab={setSearchTab}
                      />
                    </SearchBarContent>
                  ))
                  .with('date', () => (
                    <SearchBarContent
                      $forcedExpanded={forcedExpanded}
                      width="100%"
                    >
                      <DateTabContent setSearchTab={setSearchTab} />
                    </SearchBarContent>
                  ))
                  .with('location', () => (
                    <SearchBarContent
                      $forcedExpanded={forcedExpanded}
                      width="100%"
                    >
                      <LocationTabContent />
                    </SearchBarContent>
                  ))
                  .otherwise(() => null)}
            </SearchBarContainer>
          ) : (
            <>
              <MobileSearchBarHeader display={{ md: 'none', xs: 'flex' }}>
                <SearchElement
                  padding="6px 6px 6px 12px"
                  minHeight="44px"
                  alignItems={'center'}
                  onClick={() => handleOpen('type')}
                  $open={open}
                  $selected={searchTab === 'type'}
                >
                  <Magnifier />
                  <SearchBarMiniHeaderContent />
                </SearchElement>
              </MobileSearchBarHeader>
              <Box display={{ xs: 'none', md: 'block' }} width="100%">
                <StyledSearchBarHeader $floating={!forcedExpanded}>
                  <MiniSearchBar
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={handleExpand}
                  >
                    <SearchBarMiniHeaderContent />
                    <MiniButton
                      onClick={handleSubmit}
                      icon={
                        <Magnifier
                          css={css`
                            min-width: 18px;
                          `}
                        />
                      }
                      variant={ButtonVariant.Primary}
                    />
                  </MiniSearchBar>
                </StyledSearchBarHeader>
              </Box>
            </>
          )}
          {isMobile && (
            <MobileContent
              forcedExpanded={forcedExpanded}
              open={open}
              searchTab={searchTab}
              setSearchTab={setSearchTab}
              handleClickAway={handleClickAway}
              handlePrevious={handlePrevious}
              handleOpen={handleOpen}
            />
          )}
        </Stack>
      </ClickAwayListener>
    </>
  )
}
